import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Copy, CheckCircle2, Archive, ArrowLeft, Loader2, User, Mail, Phone, MapPin, FileText, Tag, Sparkles, Send, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/**
 * /admin/historic , read-only Historic Customer & Order migration tab.
 *
 * Per Kayla's V2.1 brief (Feb 21): pulls EVERY customer from EVERY legacy
 * collection (console_customers, customer_accounts, manual_orders,
 * console_orders, audit_*, bloom_box_*, hq_*), deduped by email, sorted
 * alphabetically. Click a customer to open a read-only profile + every
 * order across every collection, with copy buttons on every field.
 */
const AUBERGINE = "#3E1F46";
const PLUM = "#5B2A4F";
const GOLD = "#C9A268";

export default function AdminHistoric() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [sending, setSending] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");

  const groups = React.useMemo(
    () => [...new Set(list.map((c) => (c.group || "").trim()).filter(Boolean))].sort(),
    [list]
  );
  const shown = React.useMemo(() => {
    if (groupFilter === "__none") return list.filter((c) => !(c.group || "").trim());
    if (groupFilter !== "all") return list.filter((c) => (c.group || "").trim() === groupFilter);
    return list;
  }, [list, groupFilter]);

  const setFolder = async (c, value) => {
    if (!c.email) return;
    let g = value;
    if (value === "__new") {
      const name = window.prompt("New folder name:");
      if (name === null) return;
      g = name.trim();
    }
    try {
      await api.post(`/admin/historic/by-email/${encodeURIComponent(c.email)}/group`, { group: g });
      setList((prev) => prev.map((x) => ((x.email || "") === c.email ? { ...x, group: g } : x)));
      toast.success(g ? `Filed under "${g}".` : "Removed from folder.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update folder.");
    }
  };

  const sendToLogged = (c) => {
    if (!c.email) { toast.error("This customer needs an email first."); return; }
    const p = new URLSearchParams({ email: c.email || "", name: c.name || "", historic: "1" });
    navigate(`/admin/orders/create?${p.toString()}`);
  };

  const reload = async (query) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/historic/customers", { params: query ? { q: query } : {} });
      setList(r.data?.customers || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load historic customers.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const h = setTimeout(() => reload(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  if (selectedEmail) {
    return <HistoricDetail email={selectedEmail} onBack={() => setSelectedEmail(null)} />;
  }

  return (
    <div data-testid="admin-historic" className="space-y-6">
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>
          ✦ Customers
        </div>
        <h1 className="font-serif text-4xl mt-1.5" style={{ color: AUBERGINE }}>
          Historical Customer Data
        </h1>
        <p className="font-serif italic mt-1.5 text-[#6E5849] max-w-3xl text-sm leading-relaxed">
          Every customer and every order ever stored across the legacy system, listed
          alphabetically. Sort them into folders, open a profile, or send them across to the
          Logged Historical ledger.
        </p>
        <div className="mt-3 text-xs text-[#7A6A55]">
          <span className="font-semibold" style={{ color: PLUM }}>{total}</span> historical customers found.
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-xl bg-white border rounded px-3 py-2" style={{ borderColor: "#E0D1C8" }}>
          <Search className="w-4 h-4 text-[#7A6A55]" />
          <input
            data-testid="historic-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or phone…"
            className="flex-1 bg-transparent border-0 outline-none font-serif text-sm"
            style={{ color: AUBERGINE }}
          />
        </div>
        <label className="inline-flex items-center gap-2 bg-white border rounded px-2.5 py-2" style={{ borderColor: "#E0D1C8" }}>
          <FolderOpen className="w-3.5 h-3.5 text-[#7A6A55]" />
          <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} data-testid="historic-group-filter" className="text-xs bg-transparent outline-none max-w-[150px]" style={{ color: AUBERGINE }}>
            <option value="all">All folders</option>
            <option value="__none">No folder</option>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
      </div>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E0D1C8" }}>
        <div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_2fr_1fr_auto_auto] px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-semibold text-[#7A6A55] border-b" style={{ borderColor: "#E0D1C8" }}>
          <div>Name</div>
          <div className="hidden md:block">Email</div>
          <div className="hidden md:block">Phone</div>
          <div className="hidden md:block">Sources</div>
          <div>Orders</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#7A6A55]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading every collection…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#7A6A55]">No matching customers.</div>
        ) : (
          <ul>
            {shown.map((c) => (
              <li key={c.email || c.name || Math.random()} className="border-b last:border-b-0 flex items-stretch" style={{ borderColor: "#F0E6D2" }}>
                <button
                  type="button"
                  data-testid={`historic-customer-${(c.email || c.name || "").replace(/[^a-z0-9]/gi, "")}`}
                  onClick={() => c.email && setSelectedEmail(c.email)}
                  disabled={!c.email}
                  className="flex-1 min-w-0 grid grid-cols-[1fr_auto] md:grid-cols-[2fr_2fr_1fr_auto_auto] px-4 py-3 text-left hover:bg-[#FBF6E9] transition items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="font-serif text-base truncate" style={{ color: AUBERGINE }}>
                    {c.name || <span className="italic text-[#9C8B73]">(no name)</span>}
                  </div>
                  <div className="hidden md:block text-sm text-[#3C2A1E] truncate">{c.email || <span className="italic text-[#9C8B73]">(no email &mdash; can&apos;t open)</span>}</div>
                  <div className="hidden md:block text-sm text-[#3C2A1E] truncate">{c.phone || "-"}</div>
                  <div className="hidden md:flex gap-1 flex-wrap">
                    {(c.sources || []).slice(0, 3).map((s) => (
                      <span key={s} className="text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: "#F0E6D2", color: PLUM }}>{s}</span>
                    ))}
                    {(c.sources || []).length > 3 && (
                      <span className="text-[9px] text-[#7A6A55]">+{(c.sources || []).length - 3}</span>
                    )}
                  </div>
                  <div className="text-xs font-semibold inline-flex items-center gap-1 justify-end" style={{ color: PLUM }}>
                    <Archive className="w-3.5 h-3.5" /> {c.order_count}
                  </div>
                </button>
                <div className="flex items-center gap-2 px-3 shrink-0 border-l" style={{ borderColor: "#F0E6D2" }}>
                  <select
                    value={(c.group || "").trim() || ""}
                    onChange={(e) => setFolder(c, e.target.value)}
                    disabled={!c.email}
                    data-testid={`historic-folder-${(c.email || "").replace(/[^a-z0-9]/gi, "")}`}
                    title={c.email ? "File this customer into a folder" : "Needs an email"}
                    className="text-[11px] bg-white border rounded px-1.5 py-1 max-w-[110px] disabled:opacity-40"
                    style={{ borderColor: "#E0D1C8", color: AUBERGINE }}
                  >
                    <option value="">No folder</option>
                    {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                    <option value="__new">+ New folder…</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => sendToLogged(c)}
                    disabled={!c.email || sending === c.email}
                    data-testid={`send-to-logged-${(c.email || "").replace(/[^a-z0-9]/gi, "")}`}
                    title={c.email ? "Copy all this customer's info & orders into Logged Historical Orders and move them out of this list" : "Needs an email to move"}
                    className="text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 border rounded px-2 py-1 hover:bg-[#FBF6E9] transition disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    style={{ borderColor: AUBERGINE, color: AUBERGINE }}
                  >
                    {sending === c.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    → Logged
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────
 * Read-only customer detail. Designed for Kayla to copy fields one-by-one
 * into the new Create Order flow.
 * ─────────────────────────────────────────────────────────────────────── */
function HistoricDetail({ email, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/historic/by-email/${encodeURIComponent(email)}`);
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load customer.");
      } finally { setLoading(false); }
    })();
  }, [email]);

  if (loading) {
    return <div className="p-8 text-center text-sm text-[#7A6A55]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading every record for {email}…</div>;
  }
  if (!data) return null;

  const c = data.customer || {};
  const orders = data.orders || [];
  const touches = data.touches || [];

  return (
    <div data-testid="historic-detail" className="space-y-6">
      <button type="button" onClick={onBack} data-testid="historic-back" className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: PLUM }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Back to list
      </button>

      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>
          ✦ Historic customer
        </div>
        <h1 className="font-serif text-3xl mt-1" style={{ color: AUBERGINE }}>
          {c.name || c.email || "Unnamed"}
        </h1>
      </header>

      {/* Profile card */}
      <section className="bg-white border rounded-md p-5" style={{ borderColor: "#E0D1C8" }}>
        <SectionLabel>Customer profile</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CopyRow icon={User}   label="Name"    value={c.name}    testid="copy-name" />
          <CopyRow icon={Mail}   label="Email"   value={c.email}   testid="copy-email" />
          <CopyRow icon={Phone}  label="Phone"   value={c.phone}   testid="copy-phone" />
          <CopyRow icon={MapPin} label="Address" value={c.address} testid="copy-address" multiline />
        </div>
        {c.notes && (
          <div className="mt-3">
            <CopyRow icon={FileText} label="Customer notes" value={c.notes} testid="copy-notes" multiline />
          </div>
        )}

        {/* "Where did this customer come from?" */}
        <div className="mt-4 pt-3 border-t text-xs" style={{ borderColor: "#F0E6D2" }}>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-1.5" style={{ color: PLUM }}>
            <Tag className="w-3 h-3 inline mr-1 mb-0.5" /> Source collections
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(c.rows_by_source || {}).map((s) => (
              <span key={s} className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: "#F0E6D2", color: PLUM }}>{s}</span>
            ))}
            {Object.keys(c.rows_by_source || {}).length === 0 && (
              <span className="italic text-[#7A6A55]">derived from order rows only</span>
            )}
          </div>
        </div>
      </section>

      {/* Orders */}
      <section>
        <SectionLabel>Orders ({orders.length})</SectionLabel>
        {orders.length === 0 ? (
          <div className="bg-white border rounded p-5 text-sm italic text-[#7A6A55]" style={{ borderColor: "#E0D1C8" }}>
            No historic orders for this email.
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((o, i) => (
              <OrderCard key={`${o.source_collection}-${o.id}-${i}`} order={o} customer={c} />
            ))}
          </div>
        )}
      </section>

      {/* Messages / notes / waitlist , quieter section */}
      {touches.length > 0 && (
        <section>
          <SectionLabel>Notes & messages ({touches.length})</SectionLabel>
          <ul className="space-y-2">
            {touches.map((t, i) => (
              <li key={i} className="bg-white border rounded p-3 text-sm" style={{ borderColor: "#E0D1C8" }}>
                <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>
                  {t.source} · {String(t.created_at || "").slice(0, 10) || "-"}
                </div>
                {t.subject && <div className="font-serif text-base mt-0.5" style={{ color: AUBERGINE }}>{t.subject}</div>}
                {t.message && (
                  <pre className="whitespace-pre-wrap text-sm font-serif text-[#3C2A1E] leading-relaxed bg-[#FBF6E9] border rounded p-2 mt-1.5" style={{ borderColor: "#F0E6D2" }}>{t.message}</pre>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}


function OrderCard({ order, customer }) {
  const navigate = useNavigate();
  const hasPickContent = !!(order.box_picks || order.box_love || order.box_avoid || order.box_special);

  const summary = useMemo(() => {
    const lines = [];
    if (order.order_number) lines.push(`Order #${order.order_number}`);
    if (order.created_at)    lines.push(`Placed: ${String(order.created_at).slice(0, 10)}`);
    if (order.stage)         lines.push(`Stage: ${order.stage}`);
    if (order.payment_status)lines.push(`Payment: ${order.payment_status}`);
    if (order.shipping_status)lines.push(`Shipping: ${order.shipping_status}`);
    if (order.total)         lines.push(`Total: ${order.total}`);
    if (order.items?.length) {
      lines.push("");
      lines.push("Items:");
      order.items.forEach((it) => lines.push(`  · ${it.quantity || 1} × ${it.name}${it.price ? ` (${it.price})` : ""}`));
    }
    // Box / mystery-box customer variety picks & preferences.
    // Only render when there is actual pick CONTENT (varieties/love/avoid/special).
    // Kind/tier/pick_count are metadata, not enough on their own.
    if (hasPickContent) {
      lines.push("");
      lines.push("Customer\u2019s box picks & preferences:");
      if (order.box_kind)        lines.push(`  · Kind: ${order.box_kind}`);
      if (order.box_tier)        lines.push(`  · Tier: ${order.box_tier}`);
      if (order.box_pick_count)  lines.push(`  · Pick count: ${order.box_pick_count}`);
      if (order.box_picks)       lines.push(`  · Varieties chosen:\n${indent(order.box_picks)}`);
      if (order.box_love)        lines.push(`  · Loves: ${order.box_love}`);
      if (order.box_avoid)       lines.push(`  · Avoid: ${order.box_avoid}`);
      if (order.box_special)     lines.push(`  · Special requests: ${order.box_special}`);
    }
    if (order.shipping_address) { lines.push(""); lines.push(`Shipping address: ${order.shipping_address}`); }
    if (order.payment_notes)    lines.push(`Payment notes: ${order.payment_notes}`);
    if (order.shipping_notes)   lines.push(`Shipping notes: ${order.shipping_notes}`);
    if (order.notes)            lines.push(`Notes: ${order.notes}`);
    return lines.join("\n");
  }, [order, hasPickContent]);

  const reusePicks = () => {
    // Flatten dict-shaped address (some legacy records store address as
    // {line1,line2,suburb,city,postcode,region}) into a clean multi-line string.
    const formatAddr = (a) => {
      if (!a) return "";
      if (typeof a === "string") return a;
      if (typeof a !== "object") return String(a);
      const parts = [a.line1, a.line2, a.suburb, a.city, a.region, a.postcode, a.country]
        .map((v) => (v == null ? "" : String(v).trim()))
        .filter(Boolean);
      return parts.join(", ");
    };
    // Stash everything we know so the Create Order form can prefill it.
    const picksNote = [
      order.box_picks ? `Varieties chosen:\n${order.box_picks}` : "",
      order.box_love   ? `Loves: ${order.box_love}` : "",
      order.box_avoid  ? `Avoid: ${order.box_avoid}` : "",
      order.box_special? `Special requests: ${order.box_special}` : "",
    ].filter(Boolean).join("\n");
    const payload = {
      customer_name: customer?.name || "",
      email:         customer?.email || "",
      phone:         customer?.phone || "",
      address:       formatAddr(customer?.address) || formatAddr(order.shipping_address) || "",
      hq_picks_note: picksNote,
      hq_pick_count: Number(order.box_pick_count) || 0,
      notes: [
        `Reusing picks from historic order ${order.order_number || order.id || ""}`.trim(),
        order.box_kind ? `Kind: ${order.box_kind}` : "",
        order.box_tier ? `Tier: ${order.box_tier}` : "",
      ].filter(Boolean).join(" , "),
    };
    try {
      sessionStorage.setItem("heritage-reuse-picks", JSON.stringify(payload));
      toast.success("Picks staged , opening Create Order…");
      navigate("/admin/orders/create?from_picks=1");
    } catch {
      toast.error("Couldn't stage picks.");
    }
  };

  return (
    <div className="bg-white border rounded-md p-4 space-y-2" style={{ borderColor: "#E0D1C8" }} data-testid={`historic-order-${order.id || order.order_number || "row"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="leading-tight">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: PLUM }}>
            {order.source}
          </div>
          <div className="font-serif text-lg" style={{ color: AUBERGINE }}>
            {order.order_number ? `Order #${order.order_number}` : (order.id ? `Ref ${String(order.id).slice(0, 10)}` : "Order")}
          </div>
          <div className="text-xs text-[#7A6A55]">{String(order.created_at || "").slice(0, 10) || "-"}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasPickContent && (
            <button
              type="button"
              onClick={reusePicks}
              data-testid={`reuse-picks-${order.id || "row"}`}
              className="text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 rounded px-2 py-1 transition shrink-0"
              style={{ backgroundColor: "#3E1F46", color: "#F4ECD8", border: "1px solid #3E1F46" }}
              title="Prefill a new Create Order form with this customer + their saved box picks"
            >
              <Sparkles className="w-3 h-3" />
              Reuse these picks
            </button>
          )}
          <CopyButton text={summary} label="Copy whole order" testid={`copy-order-${order.id || "row"}`} />
        </div>
      </div>

      <pre className="whitespace-pre-wrap text-sm font-serif text-[#3C2A1E] leading-relaxed bg-[#FBF6E9] border rounded p-3 mt-2 overflow-x-auto" style={{ borderColor: "#F0E6D2" }}>
{summary || "(no further detail)"}
      </pre>
    </div>
  );
}


function SectionLabel({ children }) {
  return (
    <div className="text-[10px] tracking-[0.35em] uppercase font-semibold mb-2" style={{ color: PLUM }}>
      ✦ {children}
    </div>
  );
}

function indent(s, prefix = "      ") {
  return String(s || "").split(/\r?\n/).map((l) => `${prefix}${l}`).join("\n");
}


function CopyRow({ icon: Icon, label, value, testid, multiline = false }) {
  return (
    <div className="flex items-start gap-3 py-1.5 group">
      <Icon className="w-4 h-4 mt-1 shrink-0 opacity-70" style={{ color: PLUM }} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: "#9C8B73" }}>{label}</div>
        <div
          className={multiline ? "font-serif text-sm text-[#3C2A1E] whitespace-pre-wrap break-words" : "font-serif text-sm text-[#3C2A1E] truncate"}
          data-testid={`${testid}-value`}
        >
          {value || <span className="italic text-[#9C8B73]">-</span>}
        </div>
      </div>
      {value && <CopyButton text={value} testid={testid} />}
    </div>
  );
}


function CopyButton({ text, label, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(text));
      setDone(true);
      toast.success("Copied.");
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Couldn't copy , select the text manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1.5 border rounded px-2 py-1 hover:bg-[#FBF6E9] transition shrink-0"
      style={{ borderColor: "#3E1F46", color: "#3E1F46" }}
    >
      {done ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {label || (done ? "Copied" : "Copy")}
    </button>
  );
}
